import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import TakipFormModal from './TakipFormModal';
import TakipKart from './TakipKart';
import {
    TakipDetay, ilgiliMeta, tarihEtiketi, zamanEtiketi, boyutEtiketi, hataMesaji, takipDegisti, tarihDurumu, TAKIP_DEGISTI_OLAYI,
} from './takipOrtak';
import './takip.css';

interface Props {
    todoID: number;
    onKapat: () => void;
}

const basHarfler = (ad: string) => ad.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toLocaleUpperCase('tr-TR');

const TakipDetayModal: React.FC<Props> = ({ todoID, onKapat }) => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const ben = user?.kullaniciID ?? 0;
    const [gecmis, setGecmis] = useState<number[]>([todoID]);
    const aktifID = gecmis[gecmis.length - 1];
    const [veri, setVeri] = useState<TakipDetay | null>(null);
    const [yukleniyor, setYukleniyor] = useState(true);
    const [hata, setHata] = useState('');
    const [notMetni, setNotMetni] = useState('');
    const [notGonderiliyor, setNotGonderiliyor] = useState(false);
    const [dosyaYukleniyor, setDosyaYukleniyor] = useState(false);
    const [form, setForm] = useState<null | 'duzenle' | 'talep'>(null);
    const dosyaRef = useRef<HTMLInputElement>(null);

    useEffect(() => { setGecmis([todoID]); }, [todoID]);

    const yukle = useCallback(async (sessiz = false) => {
        if (!sessiz) setYukleniyor(true);
        setHata('');
        try {
            const r = await axiosInstance.get(`/api/Takipler/${aktifID}`);
            setVeri(r.data);
        } catch (e) {
            setVeri(null);
            setHata(hataMesaji(e, 'Takip yüklenemedi.'));
        } finally {
            setYukleniyor(false);
        }
    }, [aktifID]);

    useEffect(() => { yukle(); }, [yukle]);
    useEffect(() => {
        const h = () => yukle(true);
        window.addEventListener(TAKIP_DEGISTI_OLAYI, h);
        return () => window.removeEventListener(TAKIP_DEGISTI_OLAYI, h);
    }, [yukle]);

    useEffect(() => {
        const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !form) onKapat(); };
        window.addEventListener('keydown', esc);
        return () => window.removeEventListener('keydown', esc);
    }, [onKapat, form]);

    const ac = (id: number) => setGecmis(prev => [...prev, id]);
    const geri = () => setGecmis(prev => prev.length > 1 ? prev.slice(0, -1) : prev);

    const durumDegistir = async () => {
        if (!veri) return;
        const yeni = !veri.takip.tamamlandi;
        if (yeni && veri.takip.acikAltTakipSayisi > 0 &&
            !window.confirm(`Bu takibin ${veri.takip.acikAltTakipSayisi} açık alt takibi (talebi) var. Yine de tamamlansın mı?`)) return;
        try {
            await axiosInstance.post(`/api/Takipler/${veri.takip.todoID}/durum`, { tamamlandi: yeni });
            takipDegisti();
        } catch (e) { alert(hataMesaji(e)); }
    };

    const sil = async () => {
        if (!veri) return;
        const ek = veri.takip.altTakipSayisi > 0 ? `\nAlt takipler silinmez, bağımsız takip olarak kalır.` : '';
        if (!window.confirm(`"${veri.takip.baslik}" takibi, notları ve dosyalarıyla birlikte silinsin mi?${ek}`)) return;
        try {
            await axiosInstance.delete(`/api/Takipler/${veri.takip.todoID}`);
            takipDegisti();
            if (gecmis.length > 1) geri(); else onKapat();
        } catch (e) { alert(hataMesaji(e)); }
    };

    const notEkle = async () => {
        if (!veri || !notMetni.trim()) return;
        setNotGonderiliyor(true);
        try {
            const r = await axiosInstance.post(`/api/Takipler/${veri.takip.todoID}/not`, { metin: notMetni.trim() });
            setVeri(v => v ? { ...v, notlar: [...v.notlar, r.data], takip: { ...v.takip, notSayisi: v.takip.notSayisi + 1 } } : v);
            setNotMetni('');
            takipDegisti();
        } catch (e) { alert(hataMesaji(e)); }
        finally { setNotGonderiliyor(false); }
    };

    const notSil = async (notID: number) => {
        if (!window.confirm('Not silinsin mi?')) return;
        try {
            await axiosInstance.delete(`/api/Takipler/not/${notID}`);
            setVeri(v => v ? { ...v, notlar: v.notlar.filter(n => n.notID !== notID) } : v);
            takipDegisti();
        } catch (e) { alert(hataMesaji(e)); }
    };

    const dosyaYukle = async (liste: FileList | null) => {
        if (!veri || !liste?.length) return;
        setDosyaYukleniyor(true);
        const hatalar: string[] = [];
        for (const f of Array.from(liste)) {
            if (f.size > 10 * 1024 * 1024) { hatalar.push(`${f.name}: en fazla 10 MB`); continue; }
            const fd = new FormData();
            fd.append('dosya', f);
            try {
                const r = await axiosInstance.post(`/api/Takipler/${veri.takip.todoID}/dosya`, fd);
                setVeri(v => v ? { ...v, dosyalar: [r.data, ...v.dosyalar] } : v);
            } catch (e) { hatalar.push(`${f.name}: ${hataMesaji(e, 'yüklenemedi')}`); }
        }
        setDosyaYukleniyor(false);
        if (hatalar.length) alert(hatalar.join('\n'));
        takipDegisti();
    };

    const dosyaIndir = async (dosyaID: number, ad: string) => {
        try {
            const r = await axiosInstance.get(`/api/Takipler/dosya/${dosyaID}`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url; a.download = ad;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 2000);
        } catch { alert('Dosya indirilemedi.'); }
    };

    const dosyaSil = async (dosyaID: number) => {
        if (!window.confirm('Dosya silinsin mi?')) return;
        try {
            await axiosInstance.delete(`/api/Takipler/dosya/${dosyaID}`);
            setVeri(v => v ? { ...v, dosyalar: v.dosyalar.filter(d => d.dosyaID !== dosyaID) } : v);
            takipDegisti();
        } catch (e) { alert(hataMesaji(e)); }
    };

    const t = veri?.takip;
    const meta = ilgiliMeta(t?.ilgiliTur);
    const td = t ? tarihDurumu(t) : null;

    return (
        <div className="tkp-perde detay" onMouseDown={e => { if (e.target === e.currentTarget) onKapat(); }}>
            <div className="tkp-modal">
                <div className="tkp-modal-bas">
                    {gecmis.length > 1 && (
                        <button className="tkp-kapat" style={{ fontSize: 15 }} onClick={geri} title="Geri"><i className="fas fa-arrow-left"></i></button>
                    )}
                    <i className={`fas ${t?.ustTakipID ? 'fa-hand-paper' : 'fa-tasks'}`} style={{ color: '#e67d21' }}></i>
                    <h5 title={t?.baslik}>{t?.baslik ?? 'Takip'}</h5>
                    {t && (
                        <span className={`tkp-rozet ${t.tamamlandi ? 'tamam' : td ?? 'notr'}`} style={{ fontSize: 11 }}>
                            {t.tamamlandi ? 'Tamamlandı' : td === 'geciken' ? 'Gecikti' : 'Açık'}
                        </span>
                    )}
                    <button className="tkp-kapat" onClick={onKapat}>×</button>
                </div>

                <div className="tkp-modal-govde">
                    {yukleniyor && <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}><i className="fas fa-spinner fa-spin" style={{ fontSize: 24 }}></i></div>}
                    {!yukleniyor && hata && <div className="tkp-hata">{hata}</div>}

                    {!yukleniyor && veri && t && (
                        <>
                            {(meta || veri.ustTakip) && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                                    {meta && t.ilgiliAnahtar && (
                                        <a className="tkp-ilgili" href={meta.url(t.ilgiliAnahtar)}
                                            onClick={e => { e.preventDefault(); onKapat(); navigate(meta.url(t.ilgiliAnahtar!)); }}
                                            title="Kayda git">
                                            <i className={`fas ${meta.ikon}`} style={{ color: meta.renk }}></i>
                                            <span>{meta.ad}: {t.ilgiliBaslik || t.ilgiliAnahtar}</span>
                                            <i className="fas fa-external-link-alt" style={{ fontSize: 10, color: '#9ca3af' }}></i>
                                        </a>
                                    )}
                                    {veri.ustTakip && (
                                        <button className="tkp-ilgili" style={{ border: 'none', cursor: 'pointer' }} onClick={() => ac(veri.ustTakip!.todoID)} title="Üst takibi aç">
                                            <i className="fas fa-level-up-alt" style={{ color: '#6d28d9' }}></i>
                                            <span>Üst takip: {veri.ustTakip.baslik} ({veri.ustTakip.kullaniciAdi})</span>
                                        </button>
                                    )}
                                </div>
                            )}

                            <div className="tkp-bilgi-izgara">
                                <div className="tkp-bilgi"><div className="e">{t.ustTakipID ? 'Talep edilen' : 'Sorumlu'}</div><div className="d">{t.kullaniciAdi}{t.kullaniciID === ben ? ' (ben)' : ''}</div></div>
                                <div className="tkp-bilgi"><div className="e">{t.ustTakipID ? 'Talep eden' : 'Oluşturan'}</div><div className="d">{t.olusturanAdi ?? t.kullaniciAdi}{(t.olusturanKullaniciID ?? t.kullaniciID) === ben ? ' (ben)' : ''}</div></div>
                                <div className="tkp-bilgi">
                                    <div className="e">Tarih</div>
                                    <div className="d" style={{ color: td === 'geciken' ? '#b91c1c' : undefined }}>
                                        {t.gorevTarihi ? `${tarihEtiketi(t.gorevTarihi)}${t.baslangicSaati ? ' ' + t.baslangicSaati : ''}` : '—'}
                                    </div>
                                </div>
                                {t.talepTuru && <div className="tkp-bilgi"><div className="e">Talep türü</div><div className="d">{t.talepTuru === 'SUREC' ? 'Süreç talebi' : 'Bilgi talebi'}</div></div>}
                                <div className="tkp-bilgi"><div className="e">Oluşturma</div><div className="d">{zamanEtiketi(t.olusturmaTarihi)}</div></div>
                                {t.tamamlandi && t.tamamlanmaTarihi && <div className="tkp-bilgi"><div className="e">Tamamlanma</div><div className="d">{zamanEtiketi(t.tamamlanmaTarihi)}</div></div>}
                            </div>

                            {t.aciklama && (
                                <div className="tkp-bolum">
                                    <div className="tkp-bolum-bas"><i className="fas fa-align-left"></i>Açıklama</div>
                                    <div className="tkp-aciklama">{t.aciklama}</div>
                                </div>
                            )}

                            {/* Alt takipler (talepler) */}
                            <div className="tkp-bolum">
                                <div className="tkp-bolum-bas">
                                    <i className="fas fa-sitemap"></i>Alt Takipler / Talepler
                                    {veri.altTakipler.length > 0 && <span className="say">{veri.altTakipler.length}</span>}
                                    <span className="sag"><button className="tkp-btn kucuk" onClick={() => setForm('talep')}><i className="fas fa-hand-paper"></i>Talep Et</button></span>
                                </div>
                                {veri.altTakipler.length === 0
                                    ? <div className="tkp-bos">Başka bir kullanıcıdan bilgi ya da süreç talep etmek için <b>Talep Et</b>'i kullanın.</div>
                                    : veri.altTakipler.map(a => <TakipKart key={a.todoID} takip={a} ben={ben} onAc={() => ac(a.todoID)} kucuk />)}
                            </div>

                            {/* Dosyalar */}
                            <div className="tkp-bolum">
                                <div className="tkp-bolum-bas">
                                    <i className="fas fa-paperclip"></i>Dosyalar
                                    {veri.dosyalar.length > 0 && <span className="say">{veri.dosyalar.length}</span>}
                                    <span className="sag">
                                        <input ref={dosyaRef} type="file" multiple style={{ display: 'none' }} onChange={e => { dosyaYukle(e.target.files); e.target.value = ''; }} />
                                        <button className="tkp-btn kucuk" disabled={dosyaYukleniyor} onClick={() => dosyaRef.current?.click()}>
                                            <i className={`fas ${dosyaYukleniyor ? 'fa-spinner fa-spin' : 'fa-upload'}`}></i>Dosya Ekle
                                        </button>
                                    </span>
                                </div>
                                {veri.dosyalar.length === 0 ? <div className="tkp-bos">Dosya yok.</div> : veri.dosyalar.map(d => (
                                    <div key={d.dosyaID} className="tkp-dosya">
                                        <i className="fas fa-file-alt" style={{ color: '#64748b' }}></i>
                                        <span className="ad" onClick={() => dosyaIndir(d.dosyaID, d.orijinalAd)} title="İndir">{d.orijinalAd}</span>
                                        <span className="kucuk">{boyutEtiketi(d.boyut)} · {d.yukleyenAdi} · {zamanEtiketi(d.tarih)}</span>
                                        <button className="tkp-ikon-btn" style={{ color: '#64748b' }} onClick={() => dosyaIndir(d.dosyaID, d.orijinalAd)} title="İndir"><i className="fas fa-download"></i></button>
                                        {(d.yukleyenID === ben || veri.silebilir) && (
                                            <button className="tkp-ikon-btn" onClick={() => dosyaSil(d.dosyaID)} title="Sil"><i className="fas fa-trash"></i></button>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Notlar */}
                            <div className="tkp-bolum">
                                <div className="tkp-bolum-bas">
                                    <i className="fas fa-comments"></i>Notlar
                                    {veri.notlar.length > 0 && <span className="say">{veri.notlar.length}</span>}
                                </div>
                                {veri.notlar.length === 0 && <div className="tkp-bos">Henüz not yok.</div>}
                                {veri.notlar.map(n => (
                                    <div key={n.notID} className="tkp-not">
                                        <div className="avatar" style={{ background: n.kullaniciID === ben ? '#e67d21' : '#1a2440' }}>{basHarfler(n.kullaniciAdi || '?')}</div>
                                        <div className="balon">
                                            <div className="ust">
                                                <b>{n.kullaniciAdi}</b><span>{zamanEtiketi(n.tarih)}</span>
                                                {n.kullaniciID === ben && (
                                                    <button className="tkp-ikon-btn" style={{ marginLeft: 'auto' }} onClick={() => notSil(n.notID)} title="Sil"><i className="fas fa-trash"></i></button>
                                                )}
                                            </div>
                                            <div className="metin">{n.metin}</div>
                                        </div>
                                    </div>
                                ))}
                                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 6 }}>
                                    <textarea className="tkp-girdi" rows={2} style={{ minHeight: 44 }} value={notMetni}
                                        onChange={e => setNotMetni(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) notEkle(); }}
                                        placeholder="Not ekle… (Ctrl+Enter ile gönder)" />
                                    <button className="tkp-btn ana" onClick={notEkle} disabled={notGonderiliyor || !notMetni.trim()}>
                                        <i className={`fas ${notGonderiliyor ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>Ekle
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {!yukleniyor && veri && t && (
                    <div className="tkp-modal-alt">
                        {veri.silebilir && <button className="tkp-btn kirmizi" onClick={sil} style={{ marginRight: 'auto' }}><i className="fas fa-trash"></i>Sil</button>}
                        {veri.duzenleyebilir && <button className="tkp-btn" onClick={() => setForm('duzenle')}><i className="fas fa-pen"></i>Düzenle</button>}
                        {veri.duzenleyebilir && (
                            <button className={`tkp-btn ${t.tamamlandi ? '' : 'yesil'}`} onClick={durumDegistir}>
                                <i className={`fas ${t.tamamlandi ? 'fa-undo' : 'fa-check'}`}></i>{t.tamamlandi ? 'Yeniden Aç' : 'Tamamlandı'}
                            </button>
                        )}
                    </div>
                )}
            </div>

            {form === 'duzenle' && t && <TakipFormModal mevcut={t} onKapat={() => setForm(null)} />}
            {form === 'talep' && t && <TakipFormModal ustTakip={{ todoID: t.todoID, baslik: t.baslik }} onKapat={() => setForm(null)} />}
        </div>
    );
};

export default TakipDetayModal;
